import { useCallback, useMemo, useState } from 'react';
import { Layers, Plus, RefreshCw, Search } from 'lucide-react';
import { useCommerceTaxonomy } from '../hooks/useCommerceTaxonomy';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import FamilyFormModal from '../components/taxonomy/FamilyFormModal';
import MetierFormModal from '../components/taxonomy/MetierFormModal';
import TaxonomyStatusBadge from '../components/taxonomy/TaxonomyStatusBadge';

function formatTaxonomyError(err) {
  const nested = err?.error || err?.data?.error || {};
  const code = err?.code || nested.code || err?.data?.code;
  const msg = nested.message || err?.message || 'Erreur';
  if (code === 'DUPLICATE_LABEL' || code === 'DUPLICATE_CODE' || code === 'DUPLICATE') {
    return 'Doublon : ce code ou libellé existe déjà.';
  }
  if (code === 'SYSTEM_PROTECTED' || code === 'SYSTEM_ENTITY' || code === 'SYSTEM') {
    return 'Entité système protégée (hard delete interdit).';
  }
  if (code === 'IN_USE' || code === 'ENTITY_IN_USE' || code === 'USED') {
    return 'Entité utilisée par un ou plusieurs commerces.';
  }
  if (code === 'HAS_CHILDREN') {
    return 'Impossible : des métiers sont rattachés à cette famille.';
  }
  if (code === 'CODE_IMMUTABLE') {
    return 'Le code est immuable après création.';
  }
  if (err?.status === 403) return msg;
  return msg;
}

const STATUS_FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'active', label: 'Actifs' },
  { id: 'inactive', label: 'Inactifs' },
  { id: 'deprecated', label: 'Deprecated' },
  { id: 'archived', label: 'Archivés' },
];

function matchesStatus(entity, filter) {
  if (filter === 'all') return true;
  if (filter === 'archived') return Boolean(entity.archivedAt);
  if (filter === 'deprecated') return Boolean(entity.deprecated) && !entity.archivedAt;
  if (filter === 'inactive') return entity.actif === false && !entity.archivedAt;
  if (filter === 'active') {
    return entity.actif !== false && !entity.archivedAt && !entity.deprecated;
  }
  return true;
}

export default function CommerceTaxonomy() {
  const { toasts, toast, removeToast } = useToast();
  const taxonomy = useCommerceTaxonomy();
  const [tab, setTab] = useState('families');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [familyModal, setFamilyModal] = useState(null);
  const [metierModal, setMetierModal] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const q = search.trim().toLowerCase();

  const filteredFamilies = useMemo(() => {
    return taxonomy.families.filter((f) => {
      if (!matchesStatus(f, statusFilter)) return false;
      if (!q) return true;
      const hay = [f.label, f.id, f.hint].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [taxonomy.families, statusFilter, q]);

  const filteredMetiers = useMemo(() => {
    return taxonomy.metiers.filter((m) => {
      if (familyFilter !== 'all' && (m.familyId || m.family) !== familyFilter) return false;
      if (!matchesStatus(m, statusFilter)) return false;
      if (!q) return true;
      const hay = [m.label, m.id, m.description, m.badge].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [taxonomy.metiers, familyFilter, statusFilter, q]);

  const runAction = useCallback(
    async (label, fn) => {
      setActionBusy(true);
      try {
        await fn();
        toast.success(label);
      } catch (err) {
        toast.error(formatTaxonomyError(err));
      } finally {
        setActionBusy(false);
      }
    },
    [toast],
  );

  const handleFamilySubmit = async (body) => {
    setActionBusy(true);
    try {
      if (familyModal?.mode === 'edit') {
        await taxonomy.updateFamily(familyModal.item.id, body);
        toast.success('Famille mise à jour');
      } else {
        await taxonomy.createFamily(body);
        toast.success('Famille créée');
      }
      setFamilyModal(null);
    } catch (err) {
      toast.error(formatTaxonomyError(err));
      throw err;
    } finally {
      setActionBusy(false);
    }
  };

  const handleMetierSubmit = async (body) => {
    setActionBusy(true);
    try {
      if (metierModal?.mode === 'edit') {
        await taxonomy.updateMetier(metierModal.item.id, body);
        toast.success('Métier mis à jour');
      } else {
        await taxonomy.createMetier(body);
        toast.success('Métier créé');
      }
      setMetierModal(null);
    } catch (err) {
      toast.error(formatTaxonomyError(err));
      throw err;
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Layers className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Familles & métiers</h1>
            <p className="text-sm text-gray-500">
              Gestion de la taxonomie commerce (SUPER_ADMIN)
              {taxonomy.source === 'fallback' && (
                <span className="ml-2 text-amber-700 font-medium">
                  — mode catalogue local (API indisponible)
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => taxonomy.refresh()}
          disabled={taxonomy.loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${taxonomy.loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {taxonomy.source === 'fallback' && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Taxonomie indisponible — mode catalogue local. Les actions d&apos;écriture sont
          désactivées tant que l&apos;API ne répond pas.
        </div>
      )}

      <div className="flex border-b border-gray-200 mb-4">
        {[
          { id: 'families', label: 'Familles' },
          { id: 'metiers', label: 'Métiers' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        {tab === 'metiers' && (
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="all">Toutes les familles</option>
            {taxonomy.families.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        )}
        {tab === 'families' && taxonomy.source === 'api' && (
          <button
            type="button"
            onClick={() => setFamilyModal({ mode: 'create' })}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" /> Ajouter une famille
          </button>
        )}
        {tab === 'metiers' && taxonomy.source === 'api' && (
          <button
            type="button"
            onClick={() => setMetierModal({ mode: 'create' })}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" /> Ajouter un métier
          </button>
        )}
      </div>

      {taxonomy.loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Chargement…</p>
      ) : tab === 'families' ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Libellé</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Icône</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Usage</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredFamilies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Aucune famille
                  </td>
                </tr>
              ) : (
                filteredFamilies.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{f.label}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{f.id}</td>
                    <td className="px-4 py-3 text-lg">{f.icon || '—'}</td>
                    <td className="px-4 py-3"><TaxonomyStatusBadge entity={f} /></td>
                    <td className="px-4 py-3 text-gray-600">{f.usageCount ?? 0}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      {taxonomy.source === 'api' && (
                        <>
                          <button
                            type="button"
                            disabled={actionBusy}
                            onClick={() => setFamilyModal({ mode: 'edit', item: f })}
                            className="text-xs text-indigo-700 hover:underline"
                          >
                            Modifier
                          </button>
                          {f.actif !== false && !f.archivedAt ? (
                            <button
                              type="button"
                              disabled={actionBusy}
                              onClick={() =>
                                runAction('Famille désactivée', () => taxonomy.deactivateFamily(f.id))
                              }
                              className="text-xs text-amber-700 hover:underline ml-2"
                            >
                              Désactiver
                            </button>
                          ) : (
                            !f.archivedAt && (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={() =>
                                  runAction('Famille réactivée', () => taxonomy.reactivateFamily(f.id))
                                }
                                className="text-xs text-green-700 hover:underline ml-2"
                              >
                                Réactiver
                              </button>
                            )
                          )}
                          {!f.archivedAt && f.actif === false && (
                            <button
                              type="button"
                              disabled={actionBusy}
                              onClick={() =>
                                runAction('Famille archivée', () => taxonomy.archiveFamily(f.id))
                              }
                              className="text-xs text-gray-700 hover:underline ml-2"
                            >
                              Archiver
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Libellé</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Famille</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Icône</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Usage</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMetiers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Aucun métier
                  </td>
                </tr>
              ) : (
                filteredMetiers.map((m) => {
                  const fam = taxonomy.getFamily(m.familyId || m.family);
                  return (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{m.label}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.id}</td>
                      <td className="px-4 py-3 text-gray-600">{fam?.label || m.familyId || '—'}</td>
                      <td className="px-4 py-3 text-lg">{m.icon || '—'}</td>
                      <td className="px-4 py-3"><TaxonomyStatusBadge entity={m} /></td>
                      <td className="px-4 py-3 text-gray-600">{m.usageCount ?? 0}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        {taxonomy.source === 'api' && (
                          <>
                            <button
                              type="button"
                              disabled={actionBusy}
                              onClick={() => setMetierModal({ mode: 'edit', item: m })}
                              className="text-xs text-indigo-700 hover:underline"
                            >
                              Modifier
                            </button>
                            {m.actif !== false && !m.archivedAt ? (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={() =>
                                  runAction('Métier désactivé', () => taxonomy.deactivateMetier(m.id))
                                }
                                className="text-xs text-amber-700 hover:underline ml-2"
                              >
                                Désactiver
                              </button>
                            ) : (
                              !m.archivedAt && (
                                <button
                                  type="button"
                                  disabled={actionBusy}
                                  onClick={() =>
                                    runAction('Métier réactivé', () => taxonomy.reactivateMetier(m.id))
                                  }
                                  className="text-xs text-green-700 hover:underline ml-2"
                                >
                                  Réactiver
                                </button>
                              )
                            )}
                            {!m.archivedAt && m.actif === false && (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={() =>
                                  runAction('Métier archivé', () => taxonomy.archiveMetier(m.id))
                                }
                                className="text-xs text-gray-700 hover:underline ml-2"
                              >
                                Archiver
                              </button>
                            )}
                            {!m.deprecated && !m.archivedAt && m.actif !== false && (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={() =>
                                  runAction('Métier deprecated', () => taxonomy.deprecateMetier(m.id, {}))
                                }
                                className="text-xs text-amber-800 hover:underline ml-2"
                              >
                                Deprecate
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <FamilyFormModal
        open={Boolean(familyModal)}
        onClose={() => setFamilyModal(null)}
        onSubmit={handleFamilySubmit}
        initial={familyModal?.mode === 'edit' ? familyModal.item : null}
        submitting={actionBusy}
      />
      <MetierFormModal
        open={Boolean(metierModal)}
        onClose={() => setMetierModal(null)}
        onSubmit={handleMetierSubmit}
        initial={metierModal?.mode === 'edit' ? metierModal.item : null}
        families={taxonomy.families}
        submitting={actionBusy}
      />

      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>
  );
}
